import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {SheetRangeImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeImpl';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {TextUtil} from '@mintdata-wsp/common/util/TextUtil';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {ISheetAPI, ISheetLocation, ISheetRange, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {FieldName, FrameData, IFrame, IFrameRecord} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {FrameSelector} from '@mintdata/mint-kit/impl/frames/FrameSelector';
import bind from 'bind-decorator';
import _clamp from 'lodash/clamp';
import _range from 'lodash/range';
import _sampleSize from 'lodash/sampleSize';

const EXTRACT_COLUMN_SPEC_RE = /^('(?:[^'\\]|\\')+'|.+?)(?:\s+as\s+('(?:[^'\\]|\\')+'|.+))?$/;

interface IAliasedColumn
{
    src:string;
    dest:string;
}

export class ExtractFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'etl';
    }

    @Function({
        title: 'filterInSet(inputFrame, condition)',
        tip: 'Returns a filtered version of the source frame, returning only rows that are included in the set.',
        alias: 'FILTER_IN_SET',
        example: 'FILTER_IN_SET(A1, "field", B2, "field")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame extracted by EL pattern',
            },
    })
    @bind
    public filterInSet(@Param({
                           title: 'inputFrame',
                           tip: 'The data to be filtered.',
                           type: Type.Frame
                       }) inputFrame:IFrame,
                       @Param({
                           title: 'inputFrameColumn',
                           tip: 'Column name to use as test element in set.',
                           type: Type.String
                       }) inputFrameColumn:AString,
                       @Param({
                           title: 'filterFrame',
                           tip: 'The set that should filtered data should be in.',
                           type: Type.Frame
                       }) filterFrame:IFrame,
                       @Param({
                           title: 'filterFrameColumn',
                           tip: 'Column name to use to form set of elements to check against.',
                           optional: true,
                           type: Type.String
                       }) filterFrameColumn?:AString):IValue
    {
        if( filterFrame.fieldCount == 0 )
            return Frames.fromFieldsAndData(inputFrame.getFields(), []);

        if( inputFrame.isEmpty )
            return inputFrame;

        const inputColumnName = inputFrameColumn.value;
        if( !inputFrame.findField(inputColumnName) )
            return EvalErrors.Name;

        const filterColumnName = filterFrameColumn
            ? filterFrameColumn.value
            : filterFrame.getFieldNames()[0];

        const filterSet = [];
        filterFrame.getRecords()
            .forEach((record) =>
            {
                const cell = record.getCell(filterColumnName);
                if( cell )
                    filterSet.push(cell);
            });

        const filteredData = [];
        for( const record of inputFrame.getRecords() )
        {
            const value = record.getCell(inputColumnName);
            if( filterSet.some((setEl) => setEl.isEqualTo(value)) )
                filteredData.push(record.getCells());
        }

        return Frames.fromFieldsAndData(inputFrame.getFields(), filteredData);
    }

    @Function({
        title: 'lastBy(inputFrame, groupingKey)',
        tip: 'Returns a filtered version of the source frame, returning only last rows in every group formed by specified key column.',
        alias: 'LAST_BY',
        example: 'LAST_BY(A1, "group_id")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame leaving only last elements in group',
            },
    })
    @bind
    public lastBy(@Param({
                      title: 'inputFrame',
                      tip: 'The data to be filtered.',
                      type: Type.Frame
                  }) inputFrame:IFrame,
                  @Param({
                      title: 'groupingKey',
                      tip: 'A key to use as a grouping criteria.',
                      type: Type.String
                  }) groupingKey:AString):IValue
    {
        if( inputFrame.isEmpty )
            return inputFrame;

        const keyName = groupingKey.value;
        const inputRecords = inputFrame.getRecords();

        const isRowLastInSomeGroup = [];
        const lastRowIdByKeyValue = {};
        inputRecords.forEach((record, index) =>
        {
            const keyValue = record.getCell(keyName).toString();

            let rowId = lastRowIdByKeyValue[keyValue];
            if( typeof rowId !== 'undefined' )
            {
                isRowLastInSomeGroup[rowId] = false;
            }

            lastRowIdByKeyValue[keyValue] = index;
            isRowLastInSomeGroup[index] = true;
        });

        const filteredData = inputRecords
            .filter((_, index) => isRowLastInSomeGroup[index])
            .map((record, _) => record.getCells());

        return Frames.fromFieldsAndData(inputFrame.getFields(), filteredData);
    }

    @Function({
        title: 'groupBy(inputFrame, groupingKey)',
        tip: 'Returns a grouped by specified key frame data.',
        alias: 'GROUP_BY',
        example: 'GROUP_BY(A1, "name")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame grouped by specified key',
            },
    })
    @bind
    public groupBy(@Param({
                       title: 'inputFrame',
                       tip: 'The data to be grouped.',
                       type: Type.Frame
                   }) inputFrame:IFrame,
                   @Param({
                       title: 'groupingKey',
                       tip: 'A key to use as a grouping criteria.',
                       type: SheetType.AnyIValue,
                       repeatable: true
                   }) args:IValue[]):IValue
    {
        const parseResult = this._parseExtractColumns(args, 'by_');

        if( Type.isInstanceOf(parseResult, Type.Error) )
            return parseResult as AError;

        const columns = parseResult as Array<IAliasedColumn>;

        return this._groupByColumn(inputFrame, columns);
    }

    private _groupByColumn(frame:IFrame, columns:Array<IAliasedColumn>):IFrame
    {
        const column:IAliasedColumn = columns[0];
        const groups = new Map<string, [IValue, IFrame]>();

        for( const record of frame.getRecords() )
        {
            const groupingCellValue:IValue = record.getCell(column.src);
            const groupingCellValueString:string = groupingCellValue.toString();

            if( !groups.has(groupingCellValueString) )
                groups.set(groupingCellValueString, [groupingCellValue, Frames.fromStrings(frame.getFieldNames(), [])]);

            const recordData:FrameData = [record.getCells()];

            groups.set(
                groupingCellValueString,
                [
                    groups.get(groupingCellValueString)[0],
                    groups.get(groupingCellValueString)[1].withRecords(recordData)
                ]
            );
        }

        const inputHeader:Array<FieldName> = frame.getFieldNames();
        const outputHeader:Array<string> = [column.src, column.dest];
        let result:IFrame = Frames.fromStrings(outputHeader, []);

        groups.forEach((value:[IValue, IFrame], groupingKeyValue:string) =>
        {
            if( inputHeader.indexOf(groupingKeyValue) < 0 )
            {
                const frameData:FrameData = columns.length > 1
                    ? [[
                        value[0],
                        this._groupByColumn(value[1], columns.slice(1))
                    ]]
                    : [value];
                result = result.withRecords(frameData);
            }
        });

        return result;
    }

    @Function({
        title: 'extract(inputFrame, pattern1)',
        tip: 'Extracts frame using EL pattern',
        alias: 'EXTRACT',
        example: 'EXTRACT(A1, "@field1")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame extracted by EL pattern',
            },
    })

    @bind
    public extract(@Param({title: 'inputFrame', tip: 'Input frame', type: Type.Frame}) inputFrame:IFrame,
                   @Param({title: 'pattern', tip: 'EL pattern', type: Type.String}) pattern:AString):IValue
    {

        const resultFrame = FrameSelector.select(inputFrame, pattern.toString());

        if( resultFrame.recordCount == 1 && resultFrame.fieldCount == 1 )
        {
            let column = resultFrame.getFieldNames()[0];
            return resultFrame.getRecords()[0].getCell(column);
        }

        return resultFrame;
    }

    @Function({
        title: 'extractColumns(inputFrame, column1 [, column2...])',
        tip: 'Create new frame having only selected columns',
        alias: 'EXTRACT_COLUMNS',
        example: 'EXTRACT_COLUMNS(A1, "column")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame with only selected columns',
            },
    })
    @bind
    public extractColumns(@Param({
                              title: 'inputFrame',
                              tip: 'Input frame',
                              type: Type.Frame
                          }) inputFrame:IFrame,
                          @Param({
                              title: 'columns',
                              tip: 'columns',
                              type: SheetType.AnyIValue,
                              repeatable: true
                          }) args:IValue[]):IValue | AError
    {
        const parseResult = this._parseExtractColumns(args);

        if( Type.isInstanceOf(parseResult, Type.Error) )
            return parseResult as AError;

        const parsedFields = parseResult as Array<IAliasedColumn>;
        const resultFields:Array<FrameField> = [];

        for( let i = 0; i < parsedFields.length; ++i )
        {
            const srcFieldName = parsedFields[i].src;
            const destFieldName = parsedFields[i].dest;

            const field = inputFrame.findField(srcFieldName);

            if( !field )
                return new AError('#COLUMN_NOT_FOUND');

            resultFields.push(new FrameField(destFieldName, field.type));
        }

        const resultData = inputFrame.getRecords().map((record) =>
            parsedFields.map((field) =>
                record.hasCell(field.src) ? record.getCell(field.src) : AEmpty.Value
            )
        );

        return Frames.fromFieldsAndData(resultFields, resultData);
    }

    private _parseExtractColumns(args:IValue[], destPrefix:string = ''):Array<IAliasedColumn> | AError
    {
        const columnsOrError = TextUtil.collectStringsFromIValues(args);
        if( Type.isInstanceOf(columnsOrError, Type.Error) )
            return columnsOrError as AError;

        const columns = columnsOrError as Array<string>;

        const result = [];
        const destFieldsNamesMap = {};
        for( let column of columns )
        {
            const match = column.match(EXTRACT_COLUMN_SPEC_RE);
            if( !match )
                return new AError('#INVALID_COLUMN_SPEC_FORMAT');

            const srcFieldName = TextUtil.stripQuotesInStringLiteral(match[1]);
            const destFieldName = TextUtil.stripQuotesInStringLiteral(match[2] || `${destPrefix}${srcFieldName}`);

            // if( destFieldsNamesMap[destFieldName] )
            //     return new AError('#COLUMN_NAMES_NOT_UNIQUE');

            if( !destFieldsNamesMap[destFieldName] )
            {
                result.push({src: srcFieldName, dest: destFieldName});
                destFieldsNamesMap[destFieldName] = true;
            }
        }

        return result;
    }

    @Function({
        title: 'extractColumns(inputFrame, column1 [, column2...])',
        tip: 'Create new frame omitting selected columns',
        alias: 'EXCLUDE_COLUMNS',
        example: 'EXCLUDE_COLUMNS(A1, "column")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame without selected columns',
            },
    })
    @bind
    public excludeColumns(@Param({
                              title: 'inputFrame',
                              tip: 'Input frame',
                              type: Type.Frame
                          }) inputFrame:IFrame,
                          @Param({
                              title: 'column',
                              tip: 'Column names to exclude from result frame',
                              type: Type.String,
                              repeatable: true
                          }) columns:AString[]):IValue | AError
    {
        const excludeFields:Array<string> = columns.map(column => column.value);
        const frameFields:Array<FrameField> = inputFrame.getFields();
        const resultFields:Array<FrameField> = [];

        for( const frameField of frameFields )
        {
            if( !excludeFields.includes(frameField.name) )
                resultFields.push(frameField);
        }

        const resultData = inputFrame.getRecords().map(
            (record:IFrameRecord) => resultFields.map(
                (resultField:FrameField) => record.getCell(resultField.name)
            )
        );

        return Frames.fromFieldsAndData(resultFields, resultData);
    }

    @Function({
        title: 'extract(inputFrame, targetLocation)',
        tip: 'Extracts frame to a target location',
        alias: 'EXTRACT_TO',
        example: 'EXTRACT_TO(A1:B12, C1)',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'Number of cells',
                tip: 'Returns number of cells',
            },
    })

    @bind
    public async extractTo(@Param({title: 'api', tip: 'api', type: SheetType.SheetAPI}) api:ISheetAPI,
                           @Param({title: 'inputFrame', tip: 'Input frame', type: Type.Frame}) inputFrame:IFrame,
                           @Param({
                               title: 'targetLocation',
                               tip: 'Target location',
                               type: SheetType.SheetLocation
                           }) targetLocation:ISheetLocation,
                           @Param({
                               title: 'clearRangeBeforeExtracting',
                               tip: 'Clear Range Before Extracting?',
                               type: Type.Boolean,
                               optional: true
                           })
                               isClearRangeBeforeExtracting:ABoolean,
    ):Promise<ANumber>
    {
        if( isClearRangeBeforeExtracting && isClearRangeBeforeExtracting.value )
        {
            const ctx = api.getSheetContext();
            const targetSheetId = targetLocation.sheetId;

            const firstBlankLocation = ctx.findFirstBlankRowBelow(targetLocation);
            const numColumnsInFrame = inputFrame.getFieldNames().length;
            const numColumnsToClear = firstBlankLocation.columnIndex + numColumnsInFrame - 1;

            const from = targetLocation;
            const to = new SheetLocationImpl(targetSheetId, numColumnsToClear, firstBlankLocation.rowIndex);

            await ctx.clearRange(from, to, true);
        }

        let cells = 0;

        const fields = inputFrame.getFields();
        const records = inputFrame.getRecords();
        const sheetContext = api.getSheetContext();

        const locations:ISheetLocation[] = [];
        const values:IValue[] = [];

        for( let fieldIndex = 0; fieldIndex < fields.length; fieldIndex++ )
        {
            for( let recordIndex = 0; recordIndex < records.length; recordIndex++ )
            {
                const sheetLocation = sheetContext.findCellLocationWithSheetId(targetLocation.columnIndex + fieldIndex, targetLocation.rowIndex + recordIndex, targetLocation.sheetId);
                const value = records[recordIndex].getCellAtOffset(fieldIndex);

                locations.push(sheetLocation);
                values.push(value);

                cells++;
            }
        }

        await sheetContext.setCellFormulasInBulk(locations, values);

        return ANumber.fromNumber(cells);
    }

    @Function({
        title: 'count_rows(range)',
        tip: 'Returns the number of rows in a specified frame or range.',
        alias: 'COUNT_ROWS',
        example: 'COUNT_ROWS(A1:B12)',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'Number of rows',
                tip: 'The number of rows in a specified frame or range.',
            },
    })
    @bind
    public rows(@Param({
        title: 'range',
        tip: 'The range whose row count will be returned',
        type: Type.Frame
    }) range:IFrame):ANumber
    {

        if( range.isError || range.isEmpty )
            return ANumber.fromNumber(0);

        return ANumber.fromNumber(range.recordCount);
    }

    @Function({
        title: 'index(array, row_number, [column_number])',
        tip: 'Has two forms: array form and reference form. In array form returns the value of an element in a table or an array, ' +
            'selected by the row and column number indexes. Use the array form if the first argument to INDEX is an array constant. ' +
            'In reference form returns the reference of the cell at the intersection of a particular row and column. If the reference ' +
            'is made up of nonadjacent selections, you can pick the selection to look in',
        alias: 'INDEX',
        example: 'INDEX(A1:B12,2)',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Value',
                tip: 'Value or set of values in the specified range or array',
            },
    })
    @bind
    public index(@Param({
                     title: 'sheetAPI',
                     tip: 'API',
                     type: SheetType.SheetAPI
                 }) api:ISheetAPI,
                 @Param({
                     title: 'range',
                     tip: 'Range of values to get ',
                     type: SheetType.Any
                 }) rangeList:any,
                 @Param({
                     title: 'rowNumber',
                     tip: 'Row number',
                     type: SheetType.AnyIValue
                 }) rowNumber:AInteger | AEmpty,
                 @Param({
                     title: 'columnNumber',
                     tip: 'Column number',
                     optional: true,
                     type: Type.Integer
                 }) columnNumber:AInteger,
                 @Param({
                     title: 'areaNum',
                     tip: 'A range in reference from which to return the intersection of Row_num and Column_num',
                     optional: true,
                     type: Type.Integer
                 }) areaNum:AInteger):any
    {
        if( Type.isInstanceOf(rowNumber, Type.Empty) )
        {
            return AEmpty.Value;
        }
        else
        {
            rowNumber = rowNumber as AInteger;
        }
        if( SheetLocationImpl.isSheetLocation(rangeList) || Type.isInstanceOf(rangeList, Type.Frame) )
        {
            let frameValue:IFrame;

            if( Type.isInstanceOf(rangeList, Type.Frame) )
                frameValue = rangeList;
            else
            {
                const value:IValue = api.getSheetContext().getCellValue(rangeList);

                if( !Type.isInstanceOf(value, Type.Frame) )
                    return EvalErrors.Value;

                frameValue = value as IFrame;
            }

            //reference form - had reference(s) array as Frames
            if( undefined == columnNumber || columnNumber.value === 0 )
            { //return row as a frame
                const frame:IFrame = frameValue.withRecordsSubset([rowNumber.value - 1]);

                const allValues = frame.recordCount * frame.fieldCount;
                if( allValues == 1 )
                    return frame.getCellByOffset(0, 0);

                return frame;
            }
            else
            {
                const fieldName:FieldName = frameValue.getFieldNames()[columnNumber.value - 1];
                const columnAsFrame:IFrame = frameValue.getColumnAsFrame(fieldName);

                if( undefined == rowNumber || rowNumber.value === 0 )
                {
                    const allValues = columnAsFrame.recordCount * columnAsFrame.fieldCount;
                    if( allValues == 1 )
                        return columnAsFrame.getCellByOffset(0, 0);

                    return columnAsFrame;
                }

                return columnAsFrame.getCell(fieldName, rowNumber.value - 1);
            }
        }
        else
        {
            let rangeToUse:ISheetRange;

            if( SheetRangeImpl.isSheetRange(rangeList) )
                rangeToUse = rangeList;
            else
            {
                if( undefined == areaNum )
                    areaNum = new AInteger(1);

                rangeToUse = rangeList.ranges[areaNum.value - 1];
            }

            //weird case - if range is one dimensional (1 row) we set row number to 1 and return 1 cell.
            //So in case INDEX(A1:K1,5) should return value from E1, not whole row)
            if( rangeToUse.from.rowIndex == rangeToUse.to.rowIndex && undefined == columnNumber )
            {
                columnNumber = rowNumber;
                rowNumber = new AInteger(1);
            }

            const sheetId = rangeToUse.sheetId;

            if( undefined == columnNumber || columnNumber.value == 0 )
            { // need to return range, where row index is as specified, columns from 0 to n (one-row range)
                const from:ISheetLocation = new SheetLocationImpl(sheetId, rangeToUse.from.columnIndex, rangeToUse.from.rowIndex + rowNumber.value - 1);
                const to:ISheetLocation = new SheetLocationImpl(sheetId, rangeToUse.to.columnIndex, rangeToUse.from.rowIndex + rowNumber.value - 1);

                return from.columnIndex === to.columnIndex && from.rowIndex === to.rowIndex
                    ? from
                    : new SheetRangeImpl(sheetId, from, to);
            }
            else if( undefined == rowNumber || rowNumber.value == 0 )
            { //need to return range where column index is as specified, rows from 0 to n (one-column range)
                const from:ISheetLocation = new SheetLocationImpl(sheetId, rangeToUse.from.columnIndex + columnNumber.value - 1, rangeToUse.from.rowIndex);
                const to:ISheetLocation = new SheetLocationImpl(sheetId, rangeToUse.from.columnIndex + columnNumber.value - 1, rangeToUse.to.rowIndex);

                return from.columnIndex === to.columnIndex && from.rowIndex === to.rowIndex
                    ? from
                    : new SheetRangeImpl(sheetId, from, to);
            }
            else
            { //"range" pointing to one cell
                return new SheetLocationImpl(sheetId, rangeToUse.from.columnIndex + columnNumber.value - 1, rangeToUse.from.rowIndex + rowNumber.value - 1);
            }
        }
    }

    @Function({
        title: 'choose(index_num, value1, [value2,...])',
        tip: 'Uses index_num to return a value from the list of value arguments. Use CHOOSE to select one of up to 254 values based on the index number. For example, if value1 through value7 are the days of the week, CHOOSE returns one of the days when a number between 1 and 7 is used as index_num.',
        example: 'CHOOSE(2, "foo", "bar")',
        alias: 'CHOOSE',
        icon: '',
        returns:
            {
                type: SheetType.Any,
                tip: 'Value'
            }
    })
    @bind
    public choose(@Param({
                      title: 'index_num',
                      tip: 'Specifies which value argument is selected. Index_num must be a number between 1 and 254, or a formula or reference to a cell containing a number between 1 and 254',
                      type: Type.Integer
                  }) indexNum:AInteger,
                  @Param({
                      title: 'value',
                      tip: 'Arguments from which CHOOSE selects a value or an action to perform based on index_num',
                      type: SheetType.Any,
                      repeatable: true
                  }) values:any[]):any
    {
        if( !indexNum || indexNum.value === 0 ) return EvalErrors.Value;//new AError('Index_num is not specified!');
        if( indexNum.value - 1 > values.length || indexNum.value - 1 < 0 ) return EvalErrors.Value;//new AError('Index_num is greater than number of arguments');
        return values[indexNum.value - 1];
    }

    @Function({
        title: 'COLUMNS(frame)',
        tip: 'Returns columns information for a frame.' +
            'The result is frame with "key", "name" and "type" columns, where each row shows key, name, type for each column.' +
            'Where "key" might be used as unique id for a column.',
        alias: 'COLUMNS',
        example: 'v(A1)',
        icon: '',
        returns:
            {
                title: 'columns',
                tip: 'Returns frame with columns information',
                type: Type.Frame,
            }
    })
    @bind
    public columns(
        @Param({
            title: 'frame',
            tip: 'The frame to get columns of.',
            type: Type.Frame
        }) frame:IFrame):IFrame
    {
        const uniqueKeys = {};
        const uniqueKeyFromNameFn = (name:string):string =>
        {
            let key = TextUtil.convertToSimpleId(name);
            let keyCounter = 1;
            while( uniqueKeys[key] )
                key = key + (keyCounter++);

            uniqueKeys[key] = true;

            return key;
        };

        const fields:Array<FrameField> = [
            new FrameField('key', Type.String),
            new FrameField('name', Type.String),
            new FrameField('type', Type.String)
        ];

        const data:Array<Array<IValue>> = [];
        frame.getFields().forEach((field) =>
        {
            data.push([
                new AString(uniqueKeyFromNameFn(field.name)),
                new AString(field.name),
                new AString(field.type.toString())
            ]);
        });

        return Frames.fromFieldsAndData(fields, data);
    }

    @Function({
        title: 'rowsAt(inputFrame, startRowIndex, endRowIndex)',
        tip: 'Returns a frame, that only contains rows from startRowIndex (inclusive) to endRowIndex (exclusive) from input frame.',
        alias: 'ROWS_AT',
        example: 'ROWS_AT(A1, 2, 10)',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Resulting frame',
                type: Type.Frame,
            }
    })
    @bind
    public rowsAt(@Param({
                      title: 'inputFrame',
                      tip: 'The data to get rows from.',
                      type: Type.Frame
                  }) inputFrame:IFrame,
                  @Param({
                      title: 'startRowIndex',
                      tip: 'Start row index.',
                      type: Type.Integer
                  }) startRowIndex:AInteger,
                  @Param({
                      title: 'endRowIndex',
                      tip: 'End row index.',
                      type: Type.Integer
                  }) endRowIndex:AInteger):IFrame | AError
    {
        return this._rowsAt(inputFrame, startRowIndex.value, endRowIndex.value);
    }

    @Function({
        title: 'rowAt(inputFrame, rowIndex)',
        tip: 'Returns a frame, that contains a single row from startRowIndex.',
        alias: 'ROW_AT',
        example: 'ROW_AT(A1, 2)',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Resulting frame',
                type: Type.Frame,
            }
    })
    @bind
    public rowAt(@Param({
                     title: 'inputFrame',
                     tip: 'The data to get rows from.',
                     type: Type.Frame
                 }) inputFrame:IFrame,
                 @Param({
                     title: 'rowIndex',
                     tip: 'Row index.',
                     type: Type.Integer
                 }) rowIndex:AInteger):IFrame | AError
    {
        return this._rowsAt(inputFrame, rowIndex.value, rowIndex.value + 1);
    }

    @Function({
        title: 'sample(inputFrame, maxRows)',
        tip: 'Returns a sample of the rows from input frame.',
        alias: 'SAMPLE',
        example: 'SAMPLE(A1, 1000)',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Resulting frame with sampled data',
                type: Type.Frame,
            }
    })
    @bind
    public sample(@Param({
                      title: 'inputFrame',
                      tip: 'The data to sample from.',
                      type: Type.Frame
                  }) inputFrame:IFrame,
                  @Param({
                      title: 'maxRows',
                      tip: 'Max number of rows to sample.',
                      type: Type.Integer
                  }) maxRows:AInteger):IFrame
    {
        return this._sampleData(inputFrame, maxRows.value);
    }

    @Function({
        title: 'samplePercent(inputFrame, rowsPercent)',
        tip: 'Returns a sample of the rows from input frame.',
        alias: 'SAMPLE_PERCENT',
        example: 'SAMPLE_PERCENT(A1, 42)',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Resulting frame with sampled data',
                type: Type.Frame,
            }
    })
    @bind
    public samplePercent(@Param({
                             title: 'inputFrame',
                             tip: 'The data to sample from.',
                             type: Type.Frame
                         }) inputFrame:IFrame,
                         @Param({
                             title: 'rowsPercent',
                             tip: 'Max number of rows to sample.',
                             type: Type.Number
                         }) rowsPercent:ANumber):IFrame
    {
        const percent = _clamp(rowsPercent.value, 0, 100);
        const maxRows = inputFrame.getRecords().length * percent / 100;

        return this._sampleData(inputFrame, maxRows);
    }

    private _sampleData(inputFrame:IFrame, maxRows:number)
    {
        const inputRecords = inputFrame.getRecords();
        if( inputRecords.length <= maxRows )
            return inputFrame;

        const sampleIndices = _sampleSize(_range(inputRecords.length), maxRows)
            .sort((a, b) => a - b);
        const sampleData = sampleIndices.map(index => inputRecords[index].getCells());
        return Frames.fromFieldsAndData(inputFrame.getFields(), sampleData);
    }

    private _rowsAt(inputFrame:IFrame, startIndex:number, endIndex:number):IFrame
    {
        const fields = inputFrame.getFields();
        const records = inputFrame.getRecords();
        const data = [];

        startIndex = Math.max(0, startIndex - 1);
        endIndex = Math.min(records.length, endIndex - 1);
        for( let i = startIndex; i < endIndex; ++i )
            data.push(records[i].getCells());

        return Frames.fromFieldsAndData(fields, data);
    }
}
